<template>
    <transition name="modal-fade">

        <div v-if="isVisible" class="modal-backdrop2" @click.self="handleBackdropClick">

            <div class="modal-dialog2" :class="[modalSizeClass, 'modal-custom']" role="document">

                <div class="modal-content" :class="`modal-${computedType}`">

                    <div class="modal-header" :class="`header-${computedType}`">
                        <div class="header-icon-wrapper">
                            <i :class="computedIcon" class="header-icon"></i>
                        </div>
                        <button type="button" class="close" @click="closeModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div class="modal-body">

                        <!-- Imagen opcional -->
                        <div v-if="image" class="modal-image-container">
                            <img :src="imageUrl" :alt="headerText" class="modal-image" />
                        </div>

                        <h4 v-if="headerText" class="modal-header-text" :class="{ 'text-primary': usePrimaryColorForHeader }" v-html="$t(headerText)"></h4>

                        <div class="modal-body-text">
                            <slot>
                                <div v-if="bodyText" v-html="$t(bodyText)"></div>
                            </slot>
                        </div>

                    </div>

                    <div class="modal-footer">
                        <slot name="footer">
                            <button type="button" class="btn" :class="computedButtonClass" @click="handleConfirm">
                                <span v-html="$t(buttonTextKey)"></span>
                            </button>

                            <!-- Link de acción secundaria opcional -->
                            <div v-if="showSecondaryAction" class="secondary-action-container">
                                <a
                                    href="#"
                                    class="secondary-link"
                                    @click.prevent="handleSecondaryAction"
                                    :class="{ 'loading': secondaryActionLoading }"
                                >
                                    <i v-if="secondaryActionLoading" class="fas fa-spinner fa-spin"></i>
                                    <span v-else v-html="$t(secondaryActionText)"></span>
                                </a>
                            </div>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'InfoModal',
    props: {

        visible: {
            type: Boolean,
            default: false
        },

        // Tipo de modal: 'info', 'success', 'warning', 'danger'
        type: {
            type: String,
            default: 'info',
            validator: value => ['info', 'success', 'warning', 'danger'].includes(value)
        },

        // Imagen opcional (nombre.ext en static/img/modals/)
        image: {
            type: String,
            default: ''
        },

        headerText: {
            type: String,
            default: ''
        },

        bodyText: {
            type: String,
            default: ''
        },

        buttonTextKey: {
            type: String,
            default: 'entendido'
        },

        // Clase CSS del botón (opcional, si no se pasa usa el tema del type)
        buttonClass: {
            type: String,
            default: ''
        },

        // Acción al confirmar: 'close', 'goToNews', 'goToVerifactu', 'redirectToInformes'
        action: {
            type: String,
            default: 'close',
            validator: value => ['close', 'goToNews', 'goToVerifactu', 'redirectToInformes'].includes(value)
        },

        modalSize: {
            type: String,
            default: '', // '', 'sm', 'lg', 'xl'
            validator: value => ['', 'sm', 'lg', 'xl'].includes(value)
        },

        closeOnBackdrop: {
            type: Boolean,
            default: true
        },

        // Icono personalizado (sobrescribe el icono del tipo)
        customIcon: {
            type: String,
            default: ''
        },

        // Usar color primario (#1E22AA) en el texto del header del body
        usePrimaryColorForHeader: {
            type: Boolean,
            default: false
        },

        // Mostrar link secundario opcional
        showSecondaryAction: {
            type: Boolean,
            default: false
        },

        // Texto del link secundario
        secondaryActionText: {
            type: String,
            default: 'noVolverAMostrar'
        }
    },

    data() {
        return {
            isVisible: this.visible,
            secondaryActionLoading: false
        };
    },

    computed: {
        // Tipo computado (para validación)
        computedType() {
            return this.type || 'info';
        },

        // Icono según el tipo
        computedIcon() {
            if (this.customIcon) return this.customIcon;

            const icons = {
                info: 'fas fa-info-circle',
                success: 'fas fa-check-circle',
                warning: 'fas fa-exclamation-triangle',
                danger: 'fas fa-times-circle'
            };

            return icons[this.computedType] || icons.info;
        },

        // URL de la imagen
        imageUrl() {
            if (!this.image) return '';

            return `static/img/modals/${this.image}`;
        },

        // Clase del tamaño del modal
        modalSizeClass() {
            if (!this.modalSize) return '';
            return `modal-${this.modalSize}`;
        },

        // Clase del botón (usa la personalizada o la del tipo)
        computedButtonClass() {
            if (this.buttonClass) return this.buttonClass;

            const classes = {
                info: 'btn-info',
                success: 'btn-success',
                warning: 'btn-warning',
                danger: 'btn-danger'
            };

            return classes[this.computedType] || 'btn-primary';
        }
    },

    watch: {
        visible(newVal) {
            this.isVisible = newVal;
            if (newVal) {
                document.body.classList.add('modal-open');
            } else {
                document.body.classList.remove('modal-open');
            }
        }
    },

    methods: {
        closeModal() {
            this.isVisible = false;
            this.$emit('close');
        },

        handleBackdropClick() {
            if (this.closeOnBackdrop) {
                this.closeModal();
            }
        },

        handleConfirm() {
            this.$emit('confirm');

            if (this.action === 'goToNews') {
                if (this.$route.name !== 'novedades') {
                    this.$router.push({ name: 'novedades' });
                }
            } else if (this.action === 'goToVerifactu') {
                if (this.$route.name !== 'verifactu') {
                    this.$router.push({ name: 'verifactu' });
                }
            } else if (this.action === 'redirectToInformes') {
                if (this.$route.name !== 'informes') {
                    this.$router.push({ name: 'informes' });
                }
            }

            this.closeModal();
        },

        async handleSecondaryAction() {
            this.secondaryActionLoading = true;

            try {
                this.$emit('secondary-action');
            } catch (error) {
                console.error('Error en acción secundaria:', error);
            } finally {
                this.secondaryActionLoading = false;
            }
        },

        handleLinkClick(event) {
            // Verificar si se hizo clic en un enlace o dentro de uno
            const link = event.target.closest('a');

            if (link && link.hash && link.hash.startsWith('#/')) {
                // Es un enlace de tipo hash-router, cerrar el modal
                this.closeModal();
            }
        }
    },

    beforeDestroy() {
        document.body.classList.remove('modal-open');
    }
};
</script>

<style scoped>

.modal-backdrop2 {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
    backdrop-filter: blur(2px);
}

.modal-dialog2 {
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    margin: 1.75rem auto;
    padding: 0;
}

.modal-dialog2.modal-sm {
    max-width: 350px;
}

.modal-dialog2.modal-lg {
    max-width: 700px;
}

.modal-dialog2.modal-xl {
    max-width: 1000px;
}

.modal-content {
    border: none;
    overflow: hidden;
    background-color: #ffffff;
    position: relative;
}

.modal-header {
    position: relative;
    padding: 0.5rem 3rem 0.5rem 1rem;
    border: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: left;
    overflow: hidden;
}

.modal-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
}

.header-info::before {
    background: linear-gradient(135deg, #1255b8 0%, #0D47A1 100%);
}

.header-success::before {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
}

.header-warning::before {
    background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
}

.header-danger::before {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.header-icon-wrapper {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    flex-shrink: 0;
    margin-right: 0;
    position: relative;
    z-index: 1;
}

.header-icon {
    font-size: 22px;
    color: white;
    cursor: default;
}

.modal-header .close {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0.75rem;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
    color: white;
    opacity: 0.8;
    cursor: pointer;
    padding: 0;
    margin: 0;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.modal-header .close:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.3);
}

.modal-body {
    padding: 0 2rem;
    text-align: justify;
    /* text-align-last: center; */
    background-color: white;
    /* background-color: blueviolet; */
}

.modal-image-container {
    padding: 1.5rem 0;
}

.modal-image {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-header-text {
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 1rem 0;
    line-height: 1.2;
    padding: 0;
}

.modal-header-text.text-primary {
    color: #1E22AA !important;
}

.modal-body-text {
    font-size: 1rem;
    color: #666;
    line-height: 1.4;
    /* background-color: blue; */
}

.modal-body-text>>>strong {
    font-weight: 600;
}

.modal-body-text >>> br {
    display: block;
    margin-bottom: 0.35rem;
}

.modal-body-text >>> i {
    cursor: default;
}

.modal-body-text >>> span {
    color: #1E22AA;
    font-weight: 600;
}

.modal-footer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    border: none;
    /* background-color: red; */
}

.secondary-action-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0;
}

.secondary-link {
    font-size: 0.75rem;
    color: #666;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0 0.5rem;
    border-radius: 4px;
}

.secondary-link:hover:not(.loading) {
    color: #1E22AA;
    text-decoration: underline;
}

.secondary-link.loading {
    cursor: not-allowed;
    opacity: 0.7;
    pointer-events: none;
}

.secondary-link .fa-spinner {
    font-size: 0.85rem;
}

.modal-footer .btn {
    min-width: 140px;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    /* text-transform: uppercase; */
    letter-spacing: 0.5px;
}

.btn-info {
    background: linear-gradient(135deg, #1255b8 0%, #0D47A1 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(18, 85, 184, 0.4);
}

.btn-info:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #0D47A1 0%, #0A3578 100%);
    box-shadow: 0 6px 16px rgba(18, 85, 184, 0.5);
}

.btn-success {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.btn-success:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(40, 167, 69, 0.5);
    background: linear-gradient(135deg, #218838 0%, #1c6e2e 100%);
}

.btn-warning {
    background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
    color: #212529;
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.4);
}

.btn-warning:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 193, 7, 0.5);
    background: linear-gradient(135deg, #e0a800 0%, #d39e00 100%);
}

.btn-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.btn-danger:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(220, 53, 69, 0.5);
    background: linear-gradient(135deg, #c82333 0%, #a71d2a 100%);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-dialog2,
.modal-fade-leave-active .modal-dialog2 {
    transition: transform 0.3s ease;
}

.modal-fade-enter .modal-dialog2 {
    transform: scale(0.9) translateY(-30px);
}

.modal-fade-leave-to .modal-dialog2 {
    transform: scale(0.9) translateY(-30px);
}

@media (max-width: 576px) {
    .modal-dialog2 {
        margin: 1rem;
        width: calc(100% - 2rem);
    }

    .modal-header {
        padding: 0.65rem 2.5rem 0.65rem 0.75rem;
    }

    .modal-header-text {
        font-size: 1.25rem;
    }

    .header-icon-wrapper {
        width: 38px;
        height: 38px;
    }

    .header-icon {
        font-size: 18px;
    }

    .modal-body {
        padding: 1.25rem 1.5rem;
    }

    .modal-footer {
        padding: 0 1.5rem 1.25rem;
    }

    .modal-footer .btn {
        min-width: 120px;
        padding: 0.65rem 1.5rem;
        font-size: 0.9rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Modal/InfoModal.vue